<template>
  <div class="base-multi-selectable" :class="classes">
    <div v-if="!props.readonly" class="base-multi-selectable__unselected">
      <div
        v-for="item in options.unselected"
        :key="item.value"
        class="base-multi-selectable__item"
        @click="handleAddOption(item)"
      >
        <slot name="unselected" v-bind="{ item }" />
      </div>
      <div v-if="!options.unselected.length" class="base-multi-selectable__empty">
        <slot name="unselected-empty">
          Выбор не доступен
        </slot>
      </div>
    </div>
    <BaseDivider v-if="!props.readonly" />
    <div class="base-multi-selectable__selected">
      <template v-if="options.selected.length">
        <div
          v-for="item in options.selected"
          :key="item.value"
          class="base-multi-selectable__item"
          @click="handleRemoveOption(item)"
        >
          <slot name="selected" v-bind="{ item }" />
        </div>
      </template>
      <div
        v-if="!options.selected.length && $slots['selected-empty']"
        class="base-multi-selectable__empty"
      >
        <slot name="selected-empty" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import BaseDivider from '../divider/index.vue'

export interface OptionType {
  label: string
  value: string
  icon?: string
}

interface Props {
  options: OptionType[]
  variant?: 'secondary' | 'light'
  readonly?: boolean
  layout?: 'wrap' | '2'
  startWithSelected?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'secondary',
  layout: 'wrap',
})
const emit = defineEmits<{
  (e: 'added', item: OptionType): void
  (e: 'removed', item: OptionType): void
}>()
const model = defineModel<Array<string>>({ required: true })
const classes = computed(() => ({
  [`base-multi-selectable--variant-${props.variant}`]: true,
  [`base-multi-selectable--layout-${props.layout}`]: true,
  'base-multi-selectable--start-with-selected': props.startWithSelected,
}))
const options = computed(() => {
  const selected = []
  const unselected = []
  for (const item of props.options) {
    if (model.value.includes(item.value)) {
      selected.push(item)
    }
    else {
      unselected.push(item)
    }
  }

  return {
    selected,
    unselected,
  }
})

function handleAddOption(item: OptionType) {
  if (props.readonly) {
    return
  }

  model.value = [...model.value, item.value]
  emit('added', item)
}

function handleRemoveOption(item: OptionType) {
  if (props.readonly) {
    return
  }

  model.value = model.value.filter(value => value !== item.value)
  emit('removed', item)
}
</script>

<style lang="scss" scoped>
.base-multi-selectable {
  border-radius: var(--radius-xs);
  display: flex;
  flex-direction: column;
  gap: rem(14px);

  &__unselected,
  &__selected {
    display: flex;
    flex-wrap: wrap;
    gap: rem(10px);
  }

  &__empty {
    @include typo-text-secondary;

    font-weight: 400;
    color: var(--color-foreground-caption);
  }

  &--variant {
    &-ligth {
      background-color: var(--color-surface-bg);
    }

    &-secondary {
      background-color: var(--color-surface-placeholder);
    }
  }

  &--layout {
    &-2 {
      .base-multi-selectable__item {
        flex: 0 1 calc(100% / 2 - 7px);
        width: 100%;
        min-width: 0;

        @include bp-media('xs') {
          flex: 0 1 100%;
        }
      }
    }
  }

  &--start-with-selected {
    > *:last-child {
      order: 1;
    }

    &:deep(.base-divider) {
      order: 2;
    }

    > *:first-child {
      order: 3;
    }
  }
}
</style>
